import React from 'react';
import moment from 'moment';
import { convertFromUTC } from '../../../util/convert_timezone';
import TechBadge from '../../common/TechBadge';

/**
 * ServiceCard - Expandable card showing a single service assignment
 *
 * Props:
 * - service: Service object with job/vehicle context
 * - expanded: Boolean - whether card is expanded
 * - onToggle: Function - called when card header is clicked
 * - onStart: Function - called when Start button clicked
 * - onFinish: Function - called when Finish button clicked
 * - actionInProgress: 'start' | 'finish' | null
 * - onShowMaterials: Function - called when materials icon is clicked
 * - onAddNotes: Function - called when Notes button is clicked
 * - timeTrackingRequired: Boolean - whether time tracking check-in is required
 * - isCheckedIn: Boolean - whether technician is currently checked in
 * - items: Array - service items (material + work) from template
 * - onToggleItem: Function - called when item checkbox is toggled
 * - dateTag: String (optional) - e.g. "Today", "Past Due", "Feb 12", "Unscheduled"
 */
const ServiceCard = ({
  service,
  expanded,
  onToggle,
  onStart,
  onFinish,
  actionInProgress,
  onShowMaterials,
  onAddNotes,
  timeTrackingRequired = false,
  isCheckedIn = true,
  items = [],
  onToggleItem,
  dateTag,
  currentUserId
}) => {
  // Step assignment context (server-enriched per service)
  const techAssignment = service.tech_assignment;
  const isLeadTech = !!service.is_lead_tech;
  const myTaskCount = service.my_task_count ?? 0;
  const totalTaskCount = techAssignment?.total_task_count ?? 0;

  // Determine whether a given item is mine, a peer's, or unassigned.
  const taskOwnership = (item) => {
    const ownerId = item.assigned_technician_id;
    if (ownerId == null) return 'unassigned';
    if (currentUserId && String(ownerId) === String(currentUserId)) return 'mine';
    return 'peer';
  };

  // Subtitle line under the customer name describing my role on this service.
  const renderAssignmentSubtitle = () => {
    if (!techAssignment) return null;
    if (totalTaskCount === 0 && isLeadTech) return null; // service has no steps — keep header clean

    if (isLeadTech) {
      if (myTaskCount === totalTaskCount) {
        return <div className="text-muted small">Lead tech ({totalTaskCount} of {totalTaskCount} steps)</div>;
      }
      return <div className="text-muted small">Lead tech — {myTaskCount} of {totalTaskCount} are yours</div>;
    }
    return (
      <div className="text-muted small">
        {myTaskCount} of {totalTaskCount} steps yours
        {techAssignment.lead_technician_name && (
          <span className="ml-2">
            Lead:{' '}
            <TechBadge
              name={techAssignment.lead_technician_name}
              color={techAssignment.lead_technician_color}
            />
          </span>
        )}
      </div>
    );
  };
  const {
    // Service info
    service_category,
    custom_description,
    service_state,

    // Job/Request context
    Year,
    Make,
    Model,
    Color,
    VIN,
    current_state,

    // Customer info
    customer_name,
    dealerName,
    customer_phone,
    phone_number,

    // Assignment info
    technician_instructions,

    // Timestamps
    started_at,
    finished_at,

    // Materials
    materials,

    // Recommended product (selected at booking)
    recommended_product_id,
    recommended_product_name,
    recommended_product_brand
  } = service;

  // Check if materials have been collected
  const hasMaterials = materials && materials.length > 0;

  // Determine display name (customer or dealer)
  const displayName = customer_name || dealerName || 'Unknown';
  const displayPhone = customer_phone || phone_number || '';

  // Vehicle string
  const vehicle = [Year, Make, Model].filter(Boolean).join(' ') || 'Unknown Vehicle';
  const vehicleColor = Color || '';

  // State badge styling
  const getStateBadge = () => {
    switch (service_state) {
      case 'new':
      case 'booked':
        return <span className="badge badge-warning">Not Started</span>;
      case 'started':
        return <span className="badge badge-primary">In Progress</span>;
      case 'finished':
        return <span className="badge badge-success">Completed</span>;
      default:
        return <span className="badge badge-secondary">{service_state}</span>;
    }
  };

  // Notes button helper
  const getNotesButton = () => {
    if (!onAddNotes || service_state === 'finished') return null;
    return (
      <button
        className="btn btn-outline-info ml-2"
        onClick={(e) => { e.stopPropagation(); onAddNotes(service); }}
        title="Add technician notes"
      >
        <i className="fa-sharp fa-regular fa-sticky-note mr-1"></i>
        Notes
      </button>
    );
  };

  // Check if car has been received (request state must be 'received' or later)
  const isCarReceived = current_state === 'received' || current_state === 'started' || current_state === 'completed';

  // Check if time tracking blocks actions
  const isBlockedByTimeTracking = timeTrackingRequired && !isCheckedIn;

  // Items logic
  const hasItems = items && items.length > 0;
  const workItems = hasItems ? items.filter(i => i.type === 'work') : [];
  const hasIncompleteWorkItems = workItems.some(i => i.status !== 'completed');
  const isBlockedByWorkItems = hasItems && hasIncompleteWorkItems;

  // Get action button
  const getActionButton = () => {
    const isStarting = actionInProgress === 'start';
    const isFinishing = actionInProgress === 'finish';

    switch (service_state) {
      case 'new':
      case 'booked':
        // If car not received, show disabled button with message
        if (!isCarReceived) {
          return (
            <div>
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-secondary"
                  disabled
                  title="Vehicle must be checked in before starting work"
                >
                  <i className="fa-sharp fa-regular fa-car mr-2"></i>
                  Awaiting Vehicle
                </button>
                {getNotesButton()}
              </div>
              <small className="text-muted mt-1 d-block">
                <i className="fa-sharp fa-regular fa-info-circle mr-1"></i>
                Vehicle must be checked in before you can start
              </small>
            </div>
          );
        }
        // Show start button - if time tracking required and not checked in, clicking will trigger enforcement modal
        return (
          <div>
            <div className="d-flex align-items-center">
              <button
                className={`btn ${isBlockedByTimeTracking ? 'btn-outline-success' : 'btn-success'}`}
                onClick={(e) => { e.stopPropagation(); onStart(); }}
                disabled={isStarting}
              >
                {isStarting ? (
                  <>
                    <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>
                    Starting...
                  </>
                ) : (
                  <>
                    <i className="fa-sharp fa-regular fa-play mr-2"></i>
                    Start Service
                  </>
                )}
              </button>
              {getNotesButton()}
            </div>
            {/* Show check-in reminder if time tracking is required but not checked in */}
            {isBlockedByTimeTracking && (
              <small className="text-warning mt-1 d-block">
                <i className="fa-sharp fa-regular fa-clock mr-1"></i>
                Check in required before starting
              </small>
            )}
          </div>
        );
      case 'started':
        return (
          <div>
            <div className="d-flex align-items-center">
              <button
                className="btn btn-primary"
                onClick={(e) => { e.stopPropagation(); onFinish(); }}
                disabled={isFinishing || isBlockedByWorkItems}
              >
                {isFinishing ? (
                  <>
                    <i className="fa-sharp fa-regular fa-spinner fa-spin mr-2"></i>
                    Completing...
                  </>
                ) : (
                  <>
                    <i className="fa-sharp fa-regular fa-check mr-2"></i>
                    Complete Service
                  </>
                )}
              </button>
              {getNotesButton()}
            </div>
            {isBlockedByWorkItems && (
              <small className="text-warning mt-1 d-block">
                <i className="fa-sharp fa-regular fa-exclamation-circle mr-1"></i>
                Complete all work steps to finish
              </small>
            )}
          </div>
        );
      case 'finished':
        return (
          <div className="text-center text-success py-2">
            <i className="fa-sharp fa-regular fa-check-circle mr-2"></i>
            Completed {finished_at && moment(finished_at).fromNow()}
          </div>
        );
      default:
        return null;
    }
  };

  // Left border color by job type — blue for retail, orange for dealer
  const borderColor = service.request_type === 'dealer' ? '#fd7e14' : '#007bff';

  return (
    <div
      className={`card mb-2 service-card ${expanded ? 'border-primary' : ''}`}
      style={{ borderLeft: `4px solid ${borderColor}` }}
    >
      {/* Collapsed Header - Always Visible */}
      <div
        className="card-body py-2"
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
      >
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            {/* Service Name - Category followed by description, with materials icon and dateTag */}
            <h6 className="mb-1 d-flex align-items-center flex-wrap">
              <span>
                {service_category}{custom_description ? ` - ${custom_description}` : ''}
                {service.quantity > 1 && (
                  <span className="text-muted ml-1">x{service.quantity}</span>
                )}
              </span>
              {dateTag && (
                <span className={`badge ml-2 ${dateTag === 'Past Due' ? 'badge-danger' : 'badge-light text-muted'}`} style={{ fontSize: '0.7rem', fontWeight: 'normal' }}>
                  {dateTag}
                </span>
              )}
              {hasMaterials && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ml-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onShowMaterials) {
                      onShowMaterials(service);
                    }
                  }}
                  title="View materials used"
                >
                  <i className="fa-sharp fa-regular fa-cubes text-info"></i>
                </button>
              )}
            </h6>

            {/* Vehicle + VIN combined on one line */}
            <div className="text-muted small">
              {vehicleColor && <span className="mr-1">{vehicleColor}</span>}
              {vehicle}
              {VIN && <span className="ml-1 font-monospace">· {VIN}</span>}
            </div>

            {/* Customer */}
            <div className="text-muted small">
              {displayName}
            </div>

            {/* Step-assignment context */}
            {renderAssignmentSubtitle()}
          </div>

          <div className="text-right">
            {/* State Badge */}
            {getStateBadge()}

            {/* Expand Indicator */}
            <div className="mt-2 text-muted">
              <i className={`fa-sharp fa-regular fa-chevron-${expanded ? 'up' : 'down'}`}></i>
            </div>
          </div>
        </div>

        {/* Instructions Preview (if any, show in collapsed state) */}
        {technician_instructions && technician_instructions.trim() && !expanded && (
          <div className="mt-2 pt-2 border-top">
            <small className="text-info">
              <i className="fa-sharp fa-regular fa-sticky-note mr-1"></i>
              <strong>Service Instructions:</strong>{' '}
              {technician_instructions.length > 50
                ? technician_instructions.substring(0, 50) + '...'
                : technician_instructions
              }
            </small>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="card-body pt-0 border-top">
          {/* Customer Phone (if available) */}
          {displayPhone && (
            <div className="row small mb-3">
              <div className="col-12">
                <strong>Phone:</strong>{' '}
                <a href={`tel:${displayPhone}`}>{displayPhone}</a>
              </div>
            </div>
          )}

          {/* Technician Instructions */}
          {technician_instructions && technician_instructions.trim() && (
            <div className="mb-3">
              <strong>Service Instructions:</strong> <strong>{technician_instructions}</strong>
            </div>
          )}

          {/* Preferred Products - Read-only list of material items */}
          {hasItems && items.filter(i => !i.type || i.type === 'material').length > 0 && (
            <div className="mb-3">
              <strong>Preferred Products:</strong>
              {items.filter(i => !i.type || i.type === 'material').map(item => {
                const owner = taskOwnership(item);
                const greyed = owner !== 'mine';
                return (
                  <div
                    key={item.id}
                    className="ml-2 mt-1 text-muted"
                    style={greyed ? { opacity: 0.55 } : undefined}
                  >
                    <i className="fa-sharp fa-regular fa-tag mr-2"></i>
                    <span>
                      {item.label}
                      {item.recommended_product_name && (
                        <span className="ml-1">
                          — {item.recommended_product_brand ? `${item.recommended_product_brand} - ` : ''}{item.recommended_product_name}
                        </span>
                      )}
                    </span>
                    {owner === 'peer' && (
                      <span className="ml-2">
                        <TechBadge
                          name={item.assigned_technician_name}
                          color={item.assigned_technician_user_color}
                        />
                      </span>
                    )}
                    {owner === 'unassigned' && totalTaskCount > 0 && (
                      <span className="ml-2 small">Unassigned</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Legacy: Recommended Product - shown when no items exist */}
          {!hasMaterials && !hasItems && recommended_product_id && recommended_product_name && (
            <div className="mb-3 p-2 bg-light rounded border-left border-success" style={{ borderLeftWidth: '4px' }}>
              <div className="text-success">
                <i className="fa-sharp fa-regular fa-check mr-2"></i>
                <strong>Recommended Product:</strong>
              </div>
              <div className="ml-4">
                {recommended_product_brand
                  ? `${recommended_product_brand} - ${recommended_product_name}`
                  : recommended_product_name}
              </div>
            </div>
          )}

          {/* Timestamps */}
          {(started_at || finished_at) && (
            <div className="row small mb-3 text-muted">
              {started_at && (
                <div className="col-6">
                  <i className="fa-sharp fa-regular fa-play mr-1"></i>
                  Started: {convertFromUTC(started_at).format('h:mm A')}
                </div>
              )}
              {finished_at && (
                <div className="col-6">
                  <i className="fa-sharp fa-regular fa-check mr-1"></i>
                  Finished: {convertFromUTC(finished_at).format('h:mm A')}
                </div>
              )}
            </div>
          )}

          {/* Work Steps - Checkable items that must be completed before finishing */}
          {hasItems && workItems.length > 0 && (
            <div className="mb-3">
              <strong>Work Steps:</strong>
              {workItems.map(item => {
                const owner = taskOwnership(item);
                const isMine = owner === 'mine' || !currentUserId; // pre-assignment world: still actionable
                const greyed = !isMine;
                const interactive = isMine && service_state === 'started';
                return (
                  <div
                    key={item.id}
                    className="d-flex align-items-center ml-2 mt-2"
                    style={greyed ? { opacity: 0.55 } : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={item.status === 'completed'}
                      onChange={(e) => {
                        e.stopPropagation();
                        if (interactive && onToggleItem) {
                          onToggleItem(item);
                        }
                      }}
                      disabled={!interactive}
                      className="mr-2"
                      style={{ width: '18px', height: '18px', cursor: interactive ? 'pointer' : 'default' }}
                    />
                    <label
                      className="mb-0"
                      style={{ cursor: interactive ? 'pointer' : 'default' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (interactive && onToggleItem) {
                          onToggleItem(item);
                        }
                      }}
                    >
                      {item.label}
                    </label>
                    {owner === 'peer' && (
                      <span className="ml-2">
                        <TechBadge
                          name={item.assigned_technician_name}
                          color={item.assigned_technician_user_color}
                        />
                      </span>
                    )}
                    {owner === 'unassigned' && totalTaskCount > 0 && (
                      <span className="ml-2 small text-muted">Unassigned</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-3">
            {getActionButton()}
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceCard;
